/**
 * CourseOverviewTab — wraps CourseForm in edit mode for the course detail page.
 * Includes entryCriteria and exitCriteria fields (extended in course-form.tsx).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { CourseForm, type CourseFormValues } from '@client/components/forms/course-form';
import { isApiError } from '@client/lib/queries/use-branches';
import { useCourse, useUpdateCourse } from '@client/lib/queries/use-courses';

interface CourseOverviewTabProps {
  courseId: string;
}

export function CourseOverviewTab({ courseId }: CourseOverviewTabProps) {
  const { data: course, isLoading, isError } = useCourse(courseId);
  const updateCourse = useUpdateCourse(courseId);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(values: CourseFormValues) {
    setFormError(null);
    try {
      const payload = {
        ...values,
        targetAgeMin: values.targetAgeMin !== '' ? Number(values.targetAgeMin) : undefined,
        targetAgeMax: values.targetAgeMax !== '' ? Number(values.targetAgeMax) : undefined,
        description: values.description || undefined,
        entryCriteria: values.entryCriteria || undefined,
        exitCriteria: values.exitCriteria || undefined,
      };
      await updateCourse.mutateAsync(payload);
      toast.success('Đã cập nhật khóa học.');
    } catch (err) {
      setFormError(isApiError(err) ? err.message : 'Lỗi máy chủ, thử lại sau.');
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (isError || !course) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Không tải được thông tin khóa học.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {formError && (
        <Alert variant="destructive">
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      )}
      <CourseForm
        isEdit
        isSubmitting={updateCourse.isPending}
        defaultValues={{
          code: course.code,
          name: course.name,
          level: course.level,
          description: course.description ?? '',
          totalSessions: course.totalSessions,
          sessionDurationMin: course.sessionDurationMin ?? 90,
          targetAgeMin: course.targetAgeMin ?? '',
          targetAgeMax: course.targetAgeMax ?? '',
          isActive: course.isActive,
          entryCriteria: course.entryCriteria ?? '',
          exitCriteria: course.exitCriteria ?? '',
        }}
        onSubmit={handleSubmit}
        onCancel={() => {/* no-op: inline edit, no cancel needed */}}
        hideCancel
      />
    </div>
  );
}
